'use server'

import { downloadBlob } from '../../download-blob'
import { getStoredToken } from '@/lib/api/session-refresh'
import { apiRequest, ApiResponse } from '../request'

export type FinancialPeriod =
    | 'today'
    | 'week'
    | 'month'
    | 'last_month'
    | 'quarter'
    | 'year'
    | 'custom'

export type FinancialTypeFilter = 'RECEIVABLE' | 'PAYABLE' | 'ALL'

/**
 * Qual data conta o período no bloco por tipo de atendimento. Espelha
 * `AppointmentDateBasis` do backend.
 */
export type AppointmentDateBasis = 'sold' | 'created' | 'scheduled' | 'paid'

export const APPOINTMENT_DATE_BASIS_LABELS: Record<AppointmentDateBasis, string> = {
    sold: 'Data da venda',
    created: 'Data de lançamento',
    scheduled: 'Data agendada',
    paid: 'Data de pagamento',
}

export const APPOINTMENT_DATE_BASIS_HINTS: Record<AppointmentDateBasis, string> = {
    sold: 'Quando o orçamento foi aprovado — ou o lançamento, se já nasceu agendado',
    created: 'Quando o atendimento foi cadastrado no sistema',
    scheduled: 'Quando o atendimento ocorre',
    paid: 'Quando a cobrança foi paga — só entram as pagas',
}

/** Base padrão. A mesma do backend e dos relatórios. */
export const DEFAULT_APPOINTMENT_DATE_BASIS: AppointmentDateBasis = 'sold'

/** `company` = filial ativa; `organization` = todas as filiais que o usuário acessa. */
export type FinancialDashboardScope = 'company' | 'organization'

export type AppointmentBreakdownType = 'CONSULTATION' | 'EXAM' | 'ORDER'

export interface FinancialDashboardQueryParams {
    period?: FinancialPeriod
    startDate?: string
    endDate?: string
    type?: FinancialTypeFilter
    /** Status da cobrança. Vazio = todos. */
    billingStatus?: string
    appointmentDateBasis?: AppointmentDateBasis
    /** Restringe o card de pedido/procedimento às cirurgias. */
    surgicalOnly?: boolean
    scope?: FinancialDashboardScope
}

/** Uma linha da tabela "O que foi vendido" (centavos). */
export interface SalesSummaryRow {
    type: AppointmentBreakdownType | 'TOTAL'
    count: number
    sold: number
    receivedUntilEnd: number
    receivedToDate: number
    outstanding: number
    healthPlanCount: number
    healthPlanSold: number
    truncated: boolean
}

export interface SalesSummary {
    rows: SalesSummaryRow[]
    total: SalesSummaryRow
    periodEnd: string
}

export interface FinancialDashboardStats {
    totalReceivable: number
    totalPayable: number
    /** Recebido no período (regime de caixa). */
    revenue: number
    /**
     * Quanto essa receita custou: repasse congelado na venda (parceiro,
     * profissional, hospital) mais insumos. **Não** é "conta a pagar quitada".
     */
    cost: number
    /**
     * Gasto da operação que não pertence a venda nenhuma: aluguel, energia,
     * material, folha. Repasse fica fora — ele é custo.
     */
    expense: number
    /** Receita − custo − despesa. */
    profit: number
    averageTicket: number
    totalCount: number
    /** Cobranças que compuseram a receita do período. */
    revenueCount: number
    /**
     * Cobranças recebidas cujo custo NÃO foi apurado (repasse sem valor congelado).
     * Entram na receita com custo zero e inflam o lucro — a tela avisa.
     */
    costUnknownCount: number
    overdueCount: number
    overdueAmount: number
    /** A receber EM ABERTO (valor − pago) com vencimento no período. */
    openReceivable: number
    /** A pagar EM ABERTO (valor − pago) com vencimento no período. */
    openPayable: number
    overdueReceivableAmount: number
    overdueReceivableCount: number
    overduePayableAmount: number
    overduePayableCount: number
}

export interface StatusBreakdown {
    status: string
    label: string
    color: string
    count: number
    totalAmount: number
    totalPaidAmount: number
    /** Parte do status que é conta a receber / a pagar. */
    receivableAmount: number
    receivableCount: number
    payableAmount: number
    payableCount: number
}

export interface PaymentMethodBreakdown {
    method: string
    label: string
    color: string
    count: number
    totalAmount: number
}

export interface TopPartner {
    id: number
    name: string
    relation: 'CLIENT' | 'PARTNER' | 'SUPPLIER' | 'HOSPITAL' | 'PROFESSIONAL'
    type: 'RECEIVABLE' | 'PAYABLE'
    count: number
    totalAmount: number
    totalPaidAmount: number
}

export interface AppointmentFinancialBreakdown {
    type: AppointmentBreakdownType
    count: number
    /** Receita líquida (centavos). */
    total: number
    cost: number
    profit: number
    averageTicket: number
    revenueCount: number
    /** Linhas com custo apurado — a base do lucro. */
    costKnownCount: number
    /** Linhas sem repasse apurado; ficam fora do lucro. */
    costUnknownCount: number
    /** Recorte parcial: bateu no teto de linhas lidas pelo backend. */
    truncated: boolean
}

export interface FinancialTimeSeriesPoint {
    date: string
    revenue: number
    cost: number
    expense: number
    profit: number
}

export interface FinancialDashboardResponse {
    stats: FinancialDashboardStats
    statusBreakdown: StatusBreakdown[]
    paymentMethodBreakdown: PaymentMethodBreakdown[]
    topClients: TopPartner[]
    topReceivablePartners: TopPartner[]
    topPayablePartners: TopPartner[]
    topSuppliers: TopPartner[]
    topReceivableHospitals: TopPartner[]
    topPayableHospitals: TopPartner[]
    topReceivableProfessionals: TopPartner[]
    topPayableProfessionals: TopPartner[]
    timeSeries: FinancialTimeSeriesPoint[]
    appointmentBreakdown: AppointmentFinancialBreakdown[]
    salesSummary: SalesSummary
    scope: FinancialDashboardScope
    companyCount: number
    appointmentDateBasis: AppointmentDateBasis
    surgicalOnly: boolean
    segment: string
    granularity: 'hour' | 'day' | 'week' | 'month'
    rangeStart: string
    rangeEnd: string
    lastUpdated: string
}

export async function getFinancialDashboard(
    params?: FinancialDashboardQueryParams,
): Promise<ApiResponse<FinancialDashboardResponse>> {
    const search = new URLSearchParams()
    if (params?.period) search.set('period', params.period)
    if (params?.startDate) search.set('startDate', params.startDate)
    if (params?.endDate) search.set('endDate', params.endDate)
    if (params?.type) search.set('type', params.type)
    if (params?.billingStatus) search.set('billingStatus', params.billingStatus)
    if (params?.appointmentDateBasis)
        search.set('appointmentDateBasis', params.appointmentDateBasis)
    if (params?.surgicalOnly) search.set('surgicalOnly', 'true')
    if (params?.scope === 'organization') search.set('scope', 'organization')
    const query = search.toString()
    return apiRequest<FinancialDashboardResponse>(
        `financial/dashboard${query ? `?${query}` : ''}`,
        {},
    )
}

export type TopRelation = TopPartner['relation']

export interface TopRelationsQueryParams extends FinancialDashboardQueryParams {
    relation: TopRelation
    /**
     * Somar também o vínculo que chega pelo agendamento — o recorte dos cards de
     * RECEITA de parceiro, profissional e hospital.
     */
    linked?: boolean
    page?: number
    pageSize?: number
}

export interface TopRelationsPage {
    items: TopPartner[]
    total: number
    page: number
    pageSize: number
}

/** A lista completa por contraparte — o "Ver todos" dos cards de top. */
export async function getFinancialTopRelations(
    params: TopRelationsQueryParams,
): Promise<ApiResponse<TopRelationsPage>> {
    const search = new URLSearchParams()
    search.set('relation', params.relation)
    if (params.period) search.set('period', params.period)
    if (params.startDate) search.set('startDate', params.startDate)
    if (params.endDate) search.set('endDate', params.endDate)
    if (params.type) search.set('type', params.type)
    if (params.billingStatus) search.set('billingStatus', params.billingStatus)
    if (params.linked) search.set('linked', 'true')
    if (params.scope === 'organization') search.set('scope', 'organization')
    if (params.page) search.set('page', String(params.page))
    if (params.pageSize) search.set('pageSize', String(params.pageSize))
    return apiRequest<TopRelationsPage>(
        `financial/dashboard/top-relations?${search.toString()}`,
        {},
    )
}

export const FINANCIAL_PERIOD_LABELS: Record<FinancialPeriod, string> = {
    today: 'Hoje',
    week: 'Esta Semana',
    month: 'Este Mês',
    last_month: 'Mês Passado',
    quarter: 'Este Trimestre',
    year: 'Este Ano',
    custom: 'Personalizado',
}

export const FINANCIAL_TYPE_LABELS: Record<FinancialTypeFilter, string> = {
    RECEIVABLE: 'A Receber',
    PAYABLE: 'A Pagar',
    ALL: 'Todos',
}

export const RELATION_LABELS: Record<TopPartner['relation'], string> = {
    CLIENT: 'Cliente',
    PARTNER: 'Parceiro',
    SUPPLIER: 'Fornecedor',
    HOSPITAL: 'Hospital',
    PROFESSIONAL: 'Profissional',
}

/* ------------------------------------------------------------------ */
/* Fechamento do mês (aba dos Relatórios)                              */
/* ------------------------------------------------------------------ */

export type MonthClosingType = 'CONSULTATION' | 'EXAM' | 'ORDER'

/** Por que sobra saldo a receber num atendimento (espelha `SalesOpenReason`). */
export type MonthClosingOpenReason =
    | 'HEALTH_PLAN'
    | 'NO_BILLING'
    | 'REFUNDED'
    | 'OVERDUE'
    | 'PARTIAL'
    | 'PENDING'
    | 'CANCELLED_CHARGE'
    | 'OTHER'

export interface MonthClosingLine {
    type: MonthClosingType
    id: number
    companyId: number
    saleDate: string
    clientName: string | null
    title: string | null
    healthPlan: boolean
    sold: number
    receivedUntilEnd: number
    receivedToDate: number
    outstanding: number
    discount: number
    cost: number | null
    profit: number | null
    billingId: number | null
    openReason: MonthClosingOpenReason
}

export interface MonthClosingGroup {
    type: MonthClosingType
    catalogId: number | null
    name: string
    quantity: number
    appointments: number
    sold: number
    receivedUntilEnd: number
    receivedToDate: number
    outstanding: number
    discount: number
    cost: number
    profit: number
    costUnknownCount: number
}

export interface MonthClosingOrphan {
    id: number
    companyId: number
    companyName: string | null
    paidAt: string | null
    payerName: string | null
    description: string | null
    received: number
    dismissedNote: string | null
}

export interface MonthClosingResponse {
    rangeStart: string
    rangeEnd: string
    segment: string
    scope: FinancialDashboardScope
    companyCount: number
    summary: SalesSummary
    totals: { discount: number; cost: number; profit: number; costUnknownCount: number }
    lines: MonthClosingLine[]
    groups: MonthClosingGroup[]
    orphans: { count: number; received: number; rows: MonthClosingOrphan[] }
    truncated: boolean
}

export interface MonthClosingParams {
    period: 'month' | 'custom'
    startDate?: string
    endDate?: string
    scope: FinancialDashboardScope
}

/**
 * Detalhamento da tabela "O que foi vendido": linhas e itens que somam exatamente o que
 * ela mostra, mais os Pix sem vínculo à parte. Permissão de relatórios.
 */
export async function getMonthClosing(
    params: MonthClosingParams,
): Promise<ApiResponse<MonthClosingResponse>> {
    const search = new URLSearchParams({ period: params.period, scope: params.scope })
    if (params.startDate) search.set('startDate', params.startDate)
    if (params.endDate) search.set('endDate', params.endDate)
    return apiRequest<MonthClosingResponse>(`financial/dashboard/month-closing?${search}`, {})
}

/** Baixa o Fechamento do mês em PDF, Excel ou CSV, no modo da tela. */
export async function exportMonthClosing(
    format: 'pdf' | 'xlsx' | 'csv',
    params: MonthClosingParams,
    view: 'grouped' | 'lines',
): Promise<void> {
    const search = new URLSearchParams({ period: params.period, scope: params.scope, view })
    if (params.startDate) search.set('startDate', params.startDate)
    if (params.endDate) search.set('endDate', params.endDate)
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const response = await fetch(
        `${baseUrl}/financial/dashboard/month-closing/export/${format}?${search}`,
        { headers: { Authorization: `Bearer ${getStoredToken() || ''}` } },
    )
    if (!response.ok) {
        // A mensagem do backend (validação, permissão) diz o que houve; o aviso genérico não.
        const body = await response.json().catch(() => null)
        const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message
        throw new Error(message || 'Não foi possível exportar o fechamento.')
    }
    const disposition = response.headers.get('Content-Disposition') || ''
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || `fechamento.${format}`
    downloadBlob(await response.blob(), filename)
}
